import type { TFunction } from "i18next";

import type { PolicyOverrides, PolicySettings, PolicySource } from "@/api/queries";

export type PolicyField = keyof PolicySettings;
export type PolicyValue = PolicySettings[PolicyField];

export type FieldSpec =
  | { key: PolicyField; kind: "bool" }
  | { key: PolicyField; kind: "choice"; options: readonly (string | number)[] }
  | { key: PolicyField; kind: "number"; min: number; max: number };

export type GroupKey = "screenshots" | "apps" | "idle" | "manual" | "retention";

/** The settings in the order the page shows them. */
export const policyGroups: { key: GroupKey; fields: FieldSpec[] }[] = [
  {
    key: "screenshots",
    fields: [
      { key: "screenshots_per_slot", kind: "choice", options: [0, 1, 2, 3] },
      { key: "screenshot_blur", kind: "choice", options: ["none", "blur"] },
      { key: "notify_on_screenshot", kind: "bool" },
    ],
  },
  {
    key: "apps",
    fields: [
      { key: "track_apps", kind: "bool" },
      { key: "track_window_titles", kind: "bool" },
      { key: "track_urls", kind: "choice", options: ["off", "domain", "full"] },
    ],
  },
  {
    key: "idle",
    fields: [
      { key: "idle_threshold_minutes", kind: "number", min: 1, max: 60 },
      { key: "idle_action", kind: "choice", options: ["ask", "discard", "keep"] },
      { key: "auto_stop_on_sleep", kind: "bool" },
    ],
  },
  {
    key: "manual",
    fields: [
      { key: "allow_manual_time", kind: "bool" },
      { key: "manual_time_requires_approval", kind: "bool" },
    ],
  },
  { key: "retention", fields: [{ key: "retention_days", kind: "number", min: 30, max: 3650 }] },
];

export const policyFields: FieldSpec[] = policyGroups.flatMap((g) => g.fields);

/** Human-readable value of a setting. */
export function formatPolicyValue(t: TFunction, key: PolicyField, value: PolicyValue): string {
  switch (key) {
    case "screenshots_per_slot":
      return value === 0 ? t("settings.policy.values.screenshotsOff") : String(value);
    case "screenshot_blur":
      return t(`settings.policy.values.blur.${value as PolicySettings["screenshot_blur"]}`);
    case "track_urls":
      return t(`settings.policy.values.track_urls.${value as PolicySettings["track_urls"]}`);
    case "idle_action":
      return t(`settings.policy.values.idle_action.${value as PolicySettings["idle_action"]}`);
    case "idle_threshold_minutes":
      return t("settings.policy.values.minutes", { count: value as number });
    case "retention_days":
      return t("settings.policy.values.days", { count: value as number });
    default:
      return value ? t("settings.policy.values.on") : t("settings.policy.values.off");
  }
}

export function formatSource(t: TFunction, source: PolicySource | undefined): string {
  switch (source?.level) {
    case "team":
      return t("settings.policy.source.team", { team: source.team_name ?? "" });
    case "user":
      return t("settings.policy.source.user");
    default:
      return t("settings.policy.source.company");
  }
}

/** Overrides as a plain map: a missing key means "inherit". */
export type Draft = Partial<Record<PolicyField, PolicyValue>>;

export function draftOf(values: PolicyOverrides | PolicySettings): Draft {
  const out: Draft = {};
  for (const f of policyFields) {
    const v = (values as Record<string, PolicyValue | null | undefined>)[f.key];
    if (v !== null && v !== undefined) out[f.key] = v;
  }
  return out;
}

export function sameDraft(a: Draft, b: Draft): boolean {
  return policyFields.every((f) => a[f.key] === b[f.key]);
}

/** Validation of number fields; returns the first invalid field. */
export function invalidField(draft: Draft): PolicyField | undefined {
  for (const f of policyFields) {
    const v = draft[f.key];
    if (f.kind === "number" && v !== undefined) {
      const n = v as number;
      if (!Number.isInteger(n) || n < f.min || n > f.max) return f.key;
    }
  }
  return undefined;
}
